/** `tour_template` payload -> the domain shape TourDetailsScreenStateMapper expects.
 *
 * This is the layer that does what the app's Room DB does: the payload carries integer
 * ids on the slots and activities, and the real objects in sibling top-level arrays.
 * Dereferencing them here keeps the mapper a straight port.
 */
import { toDomainImage, type DomainImage } from '../images';

/** `TourTimeSlot.timeline_display` — a server-driven display switch. */
export const TIMELINE_DISPLAY = { TIMELINE_TEXT: 0, TIMESLOT_TIME: 1, HIDE: 2 } as const;

export interface TourActivity {
  id: number;
  activityId: number | null;
  name: string;
  previewText: string | null;
  description: string | null;
  coverImage: DomainImage | null;
  sequence: number;
  isSelected: boolean;
  hasCoordinates: boolean;
  /** null unless BOTH lat and lng are present — the only thing that puts a marker on
   *  the map. `display_on_map` does NOT gate this: upstream marks it
   *  @Deprecated("This field should be ignored due to CP-2165") and reads coordinates
   *  straight off the activity. */
  position: { lat: number; lng: number } | null;
}

/** `TourTimeSlot.Pin` — a sealed class upstream, so exactly one shape applies. */
export type SlotPin =
  | { kind: 'numbered' }
  | { kind: 'numberedCustom'; value: number }
  | { kind: 'icon'; key: string }
  | { kind: 'iconCustom'; url: string };

export interface TourTimeSlot {
  id: number;
  time: string | null;
  description: string | null;
  timelineDisplay: number | null;
  timelineText: string | null;
  showTransport: boolean;
  transportText: string | null;
  nextTransportName: string | null;
  activities: TourActivity[];
  /** Only slots with a coordinate-bearing activity get a numbered pin. */
  hasActivitiesWithCoordinates: boolean;
  /** Which of the four pin shapes this slot's markers use. */
  pin: SlotPin;
}

export interface TourDay {
  id: number;
  sequence: number;
  daySpan: number;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  timeslots: TourTimeSlot[];
}

export interface TourDomain {
  id: number;
  name: string;
  days: TourDay[];
}

/** The card thumbnail: the activity's first image BY SEQUENCE.
 *
 *  Two traps, both of which fail silently. The `{p, s}` pair is NESTED under `image` on
 *  each row — handing the row itself to `toDomainImage` yields null and drops every
 *  thumbnail with no error. And the array is not guaranteed to arrive in sequence order,
 *  so `[0]` is not necessarily the image the app shows. The itinerary and activity
 *  adapters already do both; this is the same rule. */
function firstImage(raw: unknown): DomainImage | null {
  if (!Array.isArray(raw)) return null;
  const rows = [...(raw as Record<string, unknown>[])].sort(
    (a, b) => (num(a.sequence) ?? 0) - (num(b.sequence) ?? 0),
  );
  return toDomainImage(rows[0]?.image);
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** Index a top-level dependency array by id. */
function indexById(payload: Record<string, unknown>, key: string): Map<number, Record<string, unknown>> {
  const arr = Array.isArray(payload[key]) ? (payload[key] as Record<string, unknown>[]) : [];
  return new Map(arr.map((o) => [Number(o.id ?? 0), o]));
}

/** `pin_type` / `pin_number_mode` / `pin_custom_number` / `pin_icon_key` / `pin_icon_url`
 *  collapse into one of four shapes. Upstream reads them as a sealed `TourTimeSlot.Pin`;
 *  the wire sends them flat, so the precedence has to be reconstructed here. */
export function adaptPin(slot: Record<string, unknown>): SlotPin {
  const type = str(slot.pin_type);
  if (type && type !== 'numbered') {
    const url = str(slot.pin_icon_url);
    if (url) return { kind: 'iconCustom', url };
    const key = str(slot.pin_icon_key);
    if (key) return { kind: 'icon', key };
  }
  const custom = num(slot.pin_custom_number);
  if (str(slot.pin_number_mode) === 'custom' && custom !== null) {
    return { kind: 'numberedCustom', value: custom };
  }
  return { kind: 'numbered' };
}

export function adaptTour(payload: Record<string, unknown>): TourDomain {
  const tour = (payload.tour ?? {}) as Record<string, unknown>;
  const activities = indexById(payload, 'activity');
  const transports = indexById(payload, 'transport_type');

  const rawDays = Array.isArray(tour.days) ? (tour.days as Record<string, unknown>[]) : [];

  const days: TourDay[] = rawDays.map((d) => {
    const rawSlots = Array.isArray(d.timeslots) ? (d.timeslots as Record<string, unknown>[]) : [];

    const timeslots: TourTimeSlot[] = rawSlots.map((sl) => {
      const rawActs = Array.isArray(sl.activities)
        ? (sl.activities as Record<string, unknown>[])
        : [];

      const acts: TourActivity[] = rawActs.map((a) => {
        const activityId = num(a.activity);
        const act = activityId !== null ? activities.get(activityId) : undefined;
        const lat = act ? num(act.latitude) : null;
        const lng = act ? num(act.longitude) : null;
        return {
          id: Number(a.id ?? 0),
          activityId,
          // ActivitySerializer.get_name already prefers display_name over name.
          name: act ? (str(act.name) ?? '') : '',
          previewText: act ? str(act.preview_text) : null,
          description: act ? str(act.description) : null,
          coverImage: act ? firstImage(act.images) : null,
          sequence: num(a.sequence) ?? 0,
          isSelected: a.is_selected === true,
          hasCoordinates: lat !== null && lng !== null,
          position: lat !== null && lng !== null ? { lat, lng } : null,
        };
      });

      const nextTransportId = num(sl.next_transport);
      const transport = nextTransportId !== null ? transports.get(nextTransportId) : undefined;

      return {
        id: Number(sl.id ?? 0),
        time: str(sl.time),
        description: str(sl.description),
        timelineDisplay: num(sl.timeline_display),
        timelineText: str(sl.timeline_text),
        showTransport: sl.show_transport === true,
        transportText: str(sl.transport_text),
        nextTransportName: transport ? str(transport.name) : null,
        activities: acts,
        hasActivitiesWithCoordinates: acts.some((a) => a.hasCoordinates),
        pin: adaptPin(sl),
      };
    });

    return {
      id: Number(d.id ?? 0),
      sequence: num(d.sequence) ?? 0,
      daySpan: num(d.day_span) ?? 1,
      title: str(d.title),
      subtitle: str(d.subtitle),
      description: str(d.description),
      timeslots,
    };
  });

  return { id: Number(tour.id ?? 0), name: str(tour.name) ?? '', days };
}

/**
 * `TourDay.getSlotDisplayIndex` — the pin number.
 *
 * Counts only slots that HAVE coordinates, so map pins and list numbers stay in step.
 * Returns null for a slot with none, which is why those rows get no numbered circle.
 */
export function slotDisplayIndex(day: TourDay, slot: TourTimeSlot): number | null {
  if (!slot.hasActivitiesWithCoordinates) return null;
  let n = 0;
  for (const s of day.timeslots) {
    if (s.hasActivitiesWithCoordinates) {
      if (s.id === slot.id) return n;
      n += 1;
    }
  }
  return null;
}

/**
 * `Tour.getDatesRange(index)` for a TEMPLATE.
 *
 * The template payload strips `start_datetime` — it is per-booking state — and in
 * template mode the app freezes the start to today at local midnight. So the tab
 * labels show TODAY's real dates, and the default day is always day 1. `now` is
 * injectable so the preview can pin it.
 */
export function getDatesRange(days: TourDay[], index: number, now: Date): [Date, Date | null] {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let offset = 0;
  for (let i = 0; i < index; i += 1) offset += days[i]?.daySpan ?? 1;
  const dayStart = new Date(start);
  dayStart.setDate(start.getDate() + offset);

  const span = days[index]?.daySpan ?? 1;
  if (span <= 1) return [dayStart, null];

  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayStart.getDate() + span - 1);
  return [dayStart, dayEnd];
}
